import React, { FC, useCallback, useMemo, useRef } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Outlet, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material';
import { type TFunction } from 'i18next';
import _omit from 'lodash/omit';
import _pick from 'lodash/pick';
import { StringParam } from 'serialize-query-params';
import { useQueryParams } from 'use-query-params';
import { Schema } from 'yup';

import {
  FolderType,
  SubFolderDto,
  UpdateFolderRequestBody,
  useGetSubfolder,
  useUpdateSubfolder
} from '@/api/generated';
import BackdropLoader from '@/components/BackdropLoader';
import { ROUTER_PATHS, SETTINGS_STEP_KEYS, toOrganizationItemSettings } from '@/root/linker';
import SettingsShell, { SettingsShellRef } from '@/views/SettingsShell/SettingsShell';

import { getRolesMembersValidationSchema, getSubFolderBasicInfoValidationSchema } from './schema';
import { SubFolderData, SubFolderFieldValues } from './types';

const SUB_FOLDER_KEYS_BY_TAB = {
  [SETTINGS_STEP_KEYS.BASIC_INFO]: ['status', 'name', 'legacyId'],
  [SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS]: ['admins', 'organizationAdmins', 'userInviteEmailLanguage']
} as { [key: string]: string[] };

const ALL_SUB_FOLDER_KEYS = Object.values(SUB_FOLDER_KEYS_BY_TAB).flatMap(list => list);
const OMIT_ON_SAVE_KEYS = ['status', 'admins'];

const TRANSFORMED_ON_LOAD_KEYS = ['admins'];

const presaveTransform = (fieldValues: SubFolderDto, stepKey: SETTINGS_STEP_KEYS) => {
  const updateRequest = _pick(fieldValues, ALL_SUB_FOLDER_KEYS) as SubFolderDto;

  return _omit(_pick(updateRequest, SUB_FOLDER_KEYS_BY_TAB[stepKey]), OMIT_ON_SAVE_KEYS);
};

const preloadTransform = (subFolder: SubFolderData) => {
  const fieldValues = _omit(_pick(subFolder, ALL_SUB_FOLDER_KEYS), TRANSFORMED_ON_LOAD_KEYS) as SubFolderFieldValues;

  fieldValues.admins = subFolder?.admins?.map(({ id }) => ({ id: id! }));

  return fieldValues;
};

const subFolderItemSettingsTabs = (t: TFunction, stepToLink: (step: SETTINGS_STEP_KEYS) => string) => [
  {
    stepKey: SETTINGS_STEP_KEYS.BASIC_INFO,
    label: t('orgSettings.tabs.basicInfo'),
    link: stepToLink(SETTINGS_STEP_KEYS.BASIC_INFO)
  }
  // FIXME: enable once ready
  // {
  //   stepKey: SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS,
  //   label: t('orgSettings.tabs.rolesAndUsers'),
  //   link: stepToLink(SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS)
  // }
];

const URL_CONFIG_MAP = {
  locationSlug: StringParam
};

const SubFolderSettings: FC = () => {
  const { spacing } = useTheme();
  const { t } = useTranslation(['settings', 'common', 'validation']);
  const { organizationSlug } = useParams() as { organizationSlug: string; locationSlug: string };
  const [{ locationSlug }] = useQueryParams(URL_CONFIG_MAP);

  const {
    data: subFolder,
    isLoading: isSubFolderLoading,
    refetch: refetchSubFolder
  } = useGetSubfolder(organizationSlug, locationSlug!, {
    query: {
      enabled: !!organizationSlug && !!locationSlug
    }
  });
  const { mutateAsync: updateSubFolder, isLoading: isSubFolderUpdating } = useUpdateSubfolder();

  const formRef = useRef<SettingsShellRef<FieldValues>>(null);

  const isLoading = isSubFolderLoading || isSubFolderUpdating;

  const saveSubFolderSettings = useCallback(
    async (data: SubFolderFieldValues, stepKey: SETTINGS_STEP_KEYS) => {
      await updateSubFolder({
        organizationSlug: organizationSlug,
        subfolderSlug: locationSlug!,
        data: presaveTransform(data, stepKey) as UpdateFolderRequestBody
      });
      await refetchSubFolder();
    },
    [locationSlug, organizationSlug, refetchSubFolder, updateSubFolder]
  );

  const tabs = useMemo(
    () =>
      subFolderItemSettingsTabs(t, (stepKey: SETTINGS_STEP_KEYS) =>
        toOrganizationItemSettings({
          organizationSlug,
          locationSlug: locationSlug!,
          stepKey,
          locationType: FolderType.SUB_FOLDER
        })
      ),
    [locationSlug, organizationSlug, t]
  );

  const defaultValues = useMemo(() => subFolder && preloadTransform(subFolder as SubFolderData), [subFolder]);

  const validations = useMemo(
    () =>
      ({
        [SETTINGS_STEP_KEYS.BASIC_INFO]: getSubFolderBasicInfoValidationSchema(t),
        [SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS]: getRolesMembersValidationSchema(t)
      }) as { [key: string]: Schema<SubFolderFieldValues> },
    [t]
  );

  return (
    <>
      <SettingsShell
        ref={formRef}
        tabs={tabs}
        defaultValues={defaultValues!}
        defaultRoute={toOrganizationItemSettings({
          organizationSlug,
          stepKey: SETTINGS_STEP_KEYS.BASIC_INFO,
          locationSlug: locationSlug!,
          locationType: FolderType.SUB_FOLDER
        })}
        routePattern={ROUTER_PATHS.SUBFOLDER_SETTINGS_GENERIC}
        sx={{ px: 5, pb: 5, margin: spacing(2, 1) }}
        onSave={saveSubFolderSettings}
        validations={validations}
      >
        <Outlet />
      </SettingsShell>
      {isLoading && <BackdropLoader />}
    </>
  );
};

export default SubFolderSettings;
